'use client';

import { c as _c } from "react/compiler-runtime";
import { ButtonGroup, Checkbox, Toggle } from '@repo/ui/components';
import { useColumnDisplayChoices, useFieldSelection } from './context/column-settings.ts';
import { useTableState } from './context/table-state-provider.tsx';
import { type DisplayChoice, SHOW_OR_HIDE } from './tanstack-table';
type DisplayChoices = {
  choices: {
    choice: DisplayChoice;
    value: string;
  }[];
  columnId: string;
};

/**
 * A column's Display choices, with the option each shows, but those of a Column field it hides;
 * none for a column without any.
 */
export const useDisplayChoices = columnId => {
  const $ = _c(8);
  const {
    isShown
  } = useFieldSelection(columnId);
  const t0 = useColumnDisplayChoices(columnId);
  let t1;
  if ($[0] !== isShown || $[1] !== t0) {
    let t2;
    if ($[3] !== isShown) {
      t2 = t3 => {
        const {
          choice
        } = t3;
        return choice.field === undefined || isShown(choice.field);
      };
      $[3] = isShown;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = t0.filter(t2);
    $[0] = isShown;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const choices = t1;
  let t2;
  if ($[5] !== choices || $[6] !== columnId) {
    t2 = choices.length > 0 ? {
      choices,
      columnId
    } : undefined;
    $[5] = choices;
    $[6] = columnId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
const isShowOrHide = (choice: DisplayChoice) => choice.options.length === SHOW_OR_HIDE.length && SHOW_OR_HIDE.every(({
  value
}, index) => choice.options[index]?.value === value);

/** The options of `choices`, such as "Relative", or "Hide 3PL icon" for a Show or Hide choice. */
export const formatDisplayChoices = (choices: DisplayChoices['choices']) => choices.map(({
  choice,
  value
}) => {
  const label = choice.options.find(option => option.value === value)?.label;
  return isShowOrHide(choice) ? `${label} ${choice.label}` : label;
}).join(', ');

/**
 * For each Display choice, its label with one button per option beside it, or for a Show or Hide
 * choice one "Show …" checkbox.
 */
export const DisplayChoicesPicker = t0 => {
  const $ = _c(9);
  const {
    choices,
    columnId
  } = t0;
  const {
    setDisplayChoice
  } = useTableState();
  let t1;
  if ($[0] !== choices || $[1] !== columnId || $[2] !== setDisplayChoice) {
    let t2;
    if ($[4] !== columnId || $[5] !== setDisplayChoice) {
      t2 = t3 => {
        const {
          choice,
          value
        } = t3;
        const labelId = `table-settings-display-${columnId}-${choice.id}`;
        if (isShowOrHide(choice)) {
          return <div className="flex items-center gap-2" key={choice.id}><Checkbox checked={value === "show"} data-display-choice={true} id={labelId} onCheckedChange={checked => setDisplayChoice(columnId, choice.id, checked ? "show" : "hide")} /><label className="min-w-0 flex-1 truncate text-sm" htmlFor={labelId}>Show {choice.label}</label></div>;
        }
        return <div className="flex items-center gap-2" key={choice.id}><span className="min-w-0 flex-1 truncate text-sm" id={labelId}>{choice.label}</span><ButtonGroup aria-labelledby={labelId}>{choice.options.map(option => <Toggle className="h-7 gap-1.5 px-2 text-xs" key={option.value} onPressedChange={pressed => pressed && setDisplayChoice(columnId, choice.id, option.value)} pressed={option.value === value} size="sm" variant="outline">{option.icon}{option.label}</Toggle>)}</ButtonGroup></div>;
      };
      $[4] = columnId;
      $[5] = setDisplayChoice;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = choices.map(t2);
    $[0] = choices;
    $[1] = columnId;
    $[2] = setDisplayChoice;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <div className="flex flex-col gap-2 px-1 py-1" data-sentry-component="DisplayChoicesPicker" data-sentry-source-file="settings-display-choices.tsx">{t1}</div>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};